import { createSlice } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '../../config/constants';

const initialState = {
  user: JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || 'null'),
  accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || null,
  refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || null,
  isAuthenticated: !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Login/Register success - SINGLE SOURCE OF TRUTH for localStorage
    setCredentials: (state, action) => {
      const payload = action.payload;
      
      // ✅ Handle the actual backend response structure
      // Response: { success, data: { user, tokens: { accessToken, refreshToken } } }
      const data = payload.data || payload;
      
      const user = data.user;
      
      // ✅ Extract tokens from nested tokens object
      const tokens = data.tokens || {};
      const access_token = tokens.accessToken || data.access_token || data.accessToken;
      const refresh_token = tokens.refreshToken || data.refresh_token || data.refreshToken;

      // Validate we have the required data
      if (!user || !access_token || !refresh_token) {
        console.error('❌ Invalid credentials payload:', {
          hasUser: !!user,
          hasAccessToken: !!access_token,
          hasRefreshToken: !!refresh_token,
          payload: payload
        });
        state.error = 'Invalid response from server';
        return;
      }
      
      // Update Redux state
      state.user = user;
      state.accessToken = access_token;
      state.refreshToken = refresh_token;
      state.isAuthenticated = true;
      state.error = null;

      // Persist to localStorage - SINGLE PLACE for token storage
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

      console.log('✅ Credentials saved successfully:', {
        userId: user.id,
        email: user.email,
        role: user.role,
        hasAccessToken: !!access_token,
        hasRefreshToken: !!refresh_token,
      });
    },

    // Update user info (after profile update)
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(state.user));
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Logout - SINGLE PLACE for clearing storage
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.CART_COUNT);

      console.log('✅ Logged out - localStorage cleared');
    },
  },
});

export const {
  setCredentials,
  updateUser,
  setLoading,
  setError,
  clearError,
  logout,
} = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUserRole = (state) => state.auth.user?.role;

export default authSlice.reducer;